import {
  DatasetConnected,
  DatasetConnectionRoles,
  DatasetCreation,
  DatasetCreationEnum,
  DatasetDetails,
  DatasetFile,
  DatasetReq,
  DatasetRes,
  DeleteDatasetFileRequest,
  DownloadDatasetFileRequest,
  RenameDatasetFileRequest,
  UploadDatasetFileReq,
  ValidationRes,
} from '~/api/types';
import { formatFilterQuery } from '~/utils';
import { v2Api } from '../../v2/v2Api';

const tags = ['datasets', 'dataset-access'] as const;
export const DEFAULT_DATASETS_PAGE_SIZE = 20;

// SMUS sends scalar fields as arrays with single element (SMUS restriction)
const normalizeStringField = (value: unknown): string | null => {
  if (Array.isArray(value)) {
    const firstItem = value[0];

    return typeof firstItem === 'string' ? firstItem : null;
  }

  return typeof value === 'string' ? value : null;
};

const transformDatasetDetails = (response: { data: DatasetDetails }) => {
  const { data } = response;

  const usersFields = [
    'secondary_data_owners',
    'secondary_data_owner_network_ids',
    'data_uploaders',
    'data_uploaders_network_ids',
  ] as const;

  const transformUserField = (field: string) => {
    const value = data[field as (typeof usersFields)[number]] as string | string[] | null;
    if (!value) return null;

    return { [field]: Array.isArray(value) ? value : value?.split(',') };
  };

  const normalizedFields = {
    collection_source: normalizeStringField(data.collection_source),
  };

  return Object.assign(data, ...usersFields.map(transformUserField), normalizedFields);
};

const datasetApi = v2Api
  .enhanceEndpoints({
    addTagTypes: tags,
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getDatasets: builder.infiniteQuery<DatasetRes, Omit<DatasetReq, 'page'> & { size?: number }, number>({
        infiniteQueryOptions: {
          initialPageParam: 1,
          getNextPageParam: (lastPageParam) => {
            const currSize = lastPageParam.pagination.size;
            const currentPage = lastPageParam.pagination.page;
            const remainingSize = lastPageParam.pagination.total - currentPage * currSize;

            if (remainingSize <= 0) return undefined;

            return currentPage + 1;
          },
        },
        query: ({ pageParam, queryArg }) => {
          const size = queryArg?.size ?? DEFAULT_DATASETS_PAGE_SIZE;

          const filters: [string | string[] | null | undefined, string][] = [
            [queryArg?.status, 'access_status'],
            [queryArg?.sort, 'sort_by'],
            [queryArg?.order, 'sort_order'],
            [queryArg?.status, 'status'],
            [queryArg?.statusList, 'status_list'],
            [queryArg?.anatomyList, 'anatomy_list'],
            [queryArg?.modalityList, 'modality_list'],
            [queryArg?.ouOwnerList, 'source_ou_list'],
            [queryArg?.dataSourceCategoryList, 'collection_source_list'],
            [queryArg?.dataSourceType, 'data_format'],
          ];

          const filterQuery = filters.map(([value, key]) => formatFilterQuery(value, key)).join('');
          const searchQuery = queryArg?.search ? `&search=${encodeURIComponent(queryArg.search)}` : '';

          return `datasets${queryArg?.isMy ? '/my?' : '?'}page=${pageParam}&size=${size}${searchQuery}${filterQuery}`;
        },
        providesTags: tags,
      }),
      getDataset: builder.query<DatasetDetails, { datasetId: number }>({
        query: ({ datasetId }) => `datasets/${datasetId}`,
        transformResponse: transformDatasetDetails,
        providesTags: tags,
      }),
      getValidateDatasetName: builder.query<ValidationRes, { name: string }>({
        query: ({ name }) => `datasets/validate-name?name=${encodeURIComponent(name)}`,
        transformResponse: (response: { data: ValidationRes }) => response.data,
      }),
      getValidateMdtId: builder.query<ValidationRes, { mdt_id: string }>({
        query: ({ mdt_id }) => `datasets/validate-mdt-id?mdt_id=${encodeURIComponent(mdt_id)}`,
        transformResponse: (response: { data: ValidationRes }) => response.data,
      }),
      createDataset: builder.mutation<{ data: DatasetDetails }, DatasetCreation>({
        query: ({ ...body }) => ({
          url: `datasets/new`,
          method: 'POST',
          body,
        }),
        invalidatesTags: tags,
      }),
      editDataset: builder.mutation<{ data: DatasetDetails }, Partial<DatasetCreation> & { datasetId: number }>({
        query: ({ datasetId, ...body }) => ({
          url: `datasets/${datasetId}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: tags,
      }),
      getDatasetEnums: builder.query<{ key: string; value: string }[], { enumKey: DatasetCreationEnum }>({
        query: ({ enumKey }) => `datasets/enums/${enumKey}`,
        transformResponse: (response: { data: { key: string; value: string }[] }) => response.data,
      }),
      getDatasetPresignedUrl: builder.mutation<
        { [key: string]: string },
        { datasetId: number; fileName: string; contentType: string; supportiveDocs: boolean }
      >({
        query: ({ datasetId, fileName, contentType, supportiveDocs = false }) => ({
          url: `datasets/${datasetId}/files/presigned-url`,
          method: 'POST',
          body: { file_name: fileName, content_type: contentType, supportive_docs: supportiveDocs },
        }),
        transformResponse: (
          response: { data: { data: { url: string } } },
          _,
          arg: { datasetId: number; fileName: string },
        ) => ({
          [arg.fileName]: response.data.data.url,
        }),
      }),
      uploadDatasetFiles: builder.mutation<PromiseSettledResult<{ file: File }>[], UploadDatasetFileReq[]>({
        queryFn: async (files: UploadDatasetFileReq[]) => {
          const promises = files.map(
            ({ presignedUrl, file, onProgress }) =>
              new Promise<{ file: File }>((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                xhr.upload.addEventListener('progress', (event) => {
                  if (event.lengthComputable) {
                    const raw = Math.round((event.loaded / event.total) * 100);
                    const percent = raw >= 100 ? 99 : raw;
                    onProgress(percent);
                  }
                });

                xhr.onload = () => {
                  if (xhr.status >= 200 && xhr.status < 300) {
                    onProgress(100);
                    resolve({ file: file });
                  } else {
                    reject({ name: file.name });
                  }
                };

                xhr.onerror = () => {
                  reject({ name: file.name });
                };
                xhr.open('PUT', presignedUrl, true);
                xhr.setRequestHeader('Content-Type', file.type);
                xhr.send(file);
              }),
          );

          return {
            data: await Promise.allSettled(promises),
          };
        },

        onQueryStarted: async (filesRequests, { dispatch }) => {
          const { datasetId, supportiveDocs } = filesRequests[0];
          dispatch(
            datasetApi.util.updateQueryData('getDatasetFiles', { datasetId, supportiveDocs }, (draft) => {
              const newFiles = filesRequests.map((filesRequest) => {
                const path = `datasets/${datasetId}/${filesRequest.file.name}`;

                return {
                  date_obtained: Date.now(),
                  date_uploaded: Date.now(),
                  id: path,
                  name: filesRequest.file.name,
                  ou: '—',
                  path,
                  size: 0,
                  type: '—',
                };
              });
              draft.unshift(...newFiles);
            }),
          );
        },
        invalidatesTags: tags,
      }),
      getDatasetFiles: builder.query<DatasetFile[], { datasetId: number; supportiveDocs?: boolean }>({
        query: ({ datasetId, supportiveDocs = false }) =>
          `datasets/${datasetId}/files?supportive_docs=${supportiveDocs}`,
        transformResponse: (response: { data: DatasetFile[] }) => response.data,
        providesTags: tags,
      }),
      getDatasetInstructions: builder.query<DatasetConnected, { datasetId: number }>({
        query: ({ datasetId }) => ({
          url: `datasets/${datasetId}/connect`,
          method: 'GET',
        }),
        transformResponse: (response: { data: DatasetConnected }) => response.data,
      }),
      getDatasetConnectionRoles: builder.query<DatasetConnectionRoles, { projectId: number; datasetId: number }>({
        query: ({ projectId, datasetId }) => ({
          url: `projects/${projectId}/datasets/${datasetId}/connect`,
          method: 'GET',
        }),
        transformResponse: (response: { data: DatasetConnectionRoles }) => response.data,
      }),
      deleteDatasetFile: builder.mutation<object, DeleteDatasetFileRequest>({
        query: ({ datasetId, filePath }) => ({
          url: `datasets/${datasetId}/delete`,
          method: 'POST',
          body: {
            file_path: filePath,
          },
        }),
        invalidatesTags: tags,
      }),
      renameDatasetFile: builder.mutation<object, RenameDatasetFileRequest>({
        query: ({ datasetId, newFilePath, oldFilePath }) => ({
          url: `datasets/${datasetId}/move`,
          method: 'POST',
          body: {
            new_file_path: newFilePath,
            old_file_path: oldFilePath,
          },
        }),
        invalidatesTags: tags,
      }),
      downloadDatasetFile: builder.mutation<{ url: string }, DownloadDatasetFileRequest>({
        query: ({ datasetId, filePath }) => ({
          url: `datasets/${datasetId}/files/supportive-docs/download-url`,
          method: 'POST',
          body: { file_path: filePath },
        }),
        transformResponse: (response: { data: { data: { url: string } } }) => ({ url: response.data.data.url }),
      }),
      sendDatasetToAtlan: builder.mutation<object, { datasetId: number }>({
        query: ({ datasetId }) => ({
          url: `/datasets/${datasetId}/send-to-atlan`,
          method: 'POST',
        }),
        invalidatesTags: tags,
      }),
    }),
  });

export default datasetApi;
